import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { technicalAPI } from '../../services/api';
import { LoadingBlock, EmptyState } from '../../components/technical/TechnicalUI';
import { GitBranch, ChevronRight, AlertTriangle, CheckCircle2 } from 'lucide-react';
import useDarkReady from '../../theme/useDarkReady';

// ─── PON-WISE VIEW ────────────────────────────────────────────────────────────
// Counted in CUSTOMERS, not in ONUs. The OLT's own port report says "18 of 20 ONUs up",
// which tells a technician nothing about who is affected or where to drive; this says
// "PON 0/4 — 12 of 14 customers down, Sawera Residency", which is a fiber cut with an
// address on it. Ports are ordered worst-first (by the share of their customers that are
// down) so the one that needs attention is always the first thing on screen, and every
// card taps through to exactly those customers on the connections list.
//
// Ports with no customers hanging off them are omitted server-side — an OLT reports all of
// its slots whether or not anything is connected, and empty rows just push the real ones
// below the fold on a phone.

// A whole port down is a fiber cut or a dead port; a couple down is ordinary customer-end
// trouble. The thresholds exist to make that distinction visible without reading numbers.
function severityOf(p) {
  if (p.down === 0) return { color: '#0ca30c', label: 'All up', icon: CheckCircle2 };
  if (p.downRatio >= 0.5) return { color: '#d03b3b', label: 'Port-wide outage', icon: AlertTriangle };
  // The ratio alone isn't enough on a small port — 1 of 5 clears 20% while still being a
  // single customer's problem, so the wording only escalates once more than one is affected.
  if (p.downRatio >= 0.2 && p.down > 1) return { color: '#fab219', label: 'Multiple down', icon: AlertTriangle };
  return { color: '#fab219', label: `${p.down} down`, icon: AlertTriangle };
}

function signalColor(dbm) {
  if (dbm == null) return 'var(--c-9ca3af)';
  if (dbm < -27) return 'var(--c-991b1b)';
  if (dbm < -24) return 'var(--c-92400e)';
  return 'var(--c-065f46)';
}

function PonCard({ p }) {
  const sev = severityOf(p);
  const SevIcon = sev.icon;
  const upPct = p.customers > 0 ? (p.up / p.customers) * 100 : 0;

  return (
    <Link
      to={`/technical/connections?pon=${p.pon}`}
      style={{
        display: 'block', textDecoration: 'none', color: 'inherit',
        border: '1px solid var(--cb-e5e7eb)', borderLeft: `4px solid ${sev.color}`,
        borderRadius: 12, background: 'var(--cb-ffffff)', padding: '14px 14px 12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: '1.05rem', color: 'var(--c-111827)' }}>PON {p.pon}</div>
          {p.topArea && (
            <div style={{ fontSize: '.76rem', color: 'var(--c-6b7280)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.topArea}
            </div>
          )}
        </div>
        <span style={{
          display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, whiteSpace: 'nowrap',
          fontSize: '.72rem', fontWeight: 700, padding: '4px 9px', borderRadius: 20,
          background: `${sev.color}1a`, color: sev.color,
        }}>
          <SevIcon size={12} />{sev.label}
        </span>
        <ChevronRight size={18} color="#c4c8ce" style={{ flexShrink: 0 }} />
      </div>

      {/* Up vs down as one bar — the shape alone separates "one customer's router is off"
          from "this whole port is dark". */}
      <div style={{ display: 'flex', height: 8, borderRadius: 4, background: 'var(--cb-eef0f3)', gap: 2, overflow: 'hidden', margin: '12px 0 10px' }}>
        <div style={{ width: `${upPct}%`, background: 'var(--cb-0ca30c)', borderRadius: '4px 0 0 4px' }} />
        {p.down > 0 && <div style={{ width: `${100 - upPct}%`, background: sev.color, borderRadius: '0 4px 4px 0' }} />}
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '.78rem' }}>
        <span style={{ color: 'var(--c-6b7280)' }}>
          <strong style={{ color: 'var(--c-111827)', fontSize: '.95rem' }}>{p.customers}</strong> customers
        </span>
        <span style={{ color: 'var(--c-6b7280)' }}>
          <strong style={{ color: 'var(--c-0ca30c)', fontSize: '.95rem' }}>{p.up}</strong> up
        </span>
        <span style={{ color: 'var(--c-6b7280)' }}>
          <strong style={{ color: p.down > 0 ? 'var(--c-d03b3b)' : 'var(--c-9ca3af)', fontSize: '.95rem' }}>{p.down}</strong> down
        </span>
        {p.worstRx != null && (
          <span style={{ color: 'var(--c-6b7280)', marginLeft: 'auto' }}>
            worst <strong style={{ color: signalColor(p.worstRx) }}>{p.worstRx} dBm</strong>
          </span>
        )}
      </div>

      {/* Only spelled out when the split actually matters — a port with both kinds of fault
          needs two different responses. */}
      {p.down > 0 && (
        <div style={{ fontSize: '.73rem', color: 'var(--c-9ca3af)', marginTop: 8 }}>
          {p.fiberDown > 0 && `${p.fiberDown} fiber down`}
          {p.fiberDown > 0 && p.noSession > 0 && ' · '}
          {p.noSession > 0 && `${p.noSession} no session`}
        </div>
      )}
    </Link>
  );
}

export default function TechnicalPonOverviewPage() {
  useDarkReady();
  const [ports, setPorts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = () => technicalAPI.getPonSummary()
      .then(({ data }) => { if (mounted) setPorts(data.data || []); })
      .catch(() => { if (mounted) setPorts([]); })
      .finally(() => { if (mounted) setLoading(false); });
    load();
    // Same 15 s cadence as the home dashboard — this reads the shared 2-min ONU cache, so a
    // faster poll would show the same numbers at more cost.
    const t = setInterval(load, 15000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  if (loading) return <LoadingBlock />;
  if (!ports || ports.length === 0) {
    return <EmptyState icon={GitBranch} text="No PON data — is the OLT reachable, and are ONUs mapped to customers?" />;
  }

  const customers = ports.reduce((n, p) => n + p.customers, 0);
  const down = ports.reduce((n, p) => n + p.down, 0);
  const affected = ports.filter(p => p.down > 0).length;

  return (
    <div>
      <div className="page-header">
        <h2>PON Overview</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>
          Customers per PON port, worst port first.
        </p>
      </div>

      <div style={{
        border: '1px solid var(--cb-e5e7eb)', borderRadius: 14, background: 'var(--cb-ffffff)',
        padding: '16px 18px', marginBottom: 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '2.2rem', fontWeight: 800, lineHeight: 1, color: 'var(--c-111827)' }}>{ports.length}</span>
          <span style={{ fontSize: '.9rem', color: 'var(--c-6b7280)' }}>PON ports carrying customers</span>
        </div>
        <div style={{ fontSize: '.82rem', color: 'var(--c-6b7280)', marginTop: 8 }}>
          {customers} customers mapped · {down > 0
            ? <><strong style={{ color: 'var(--c-d03b3b)' }}>{down} down</strong> across {affected} {affected === 1 ? 'port' : 'ports'}</>
            : <strong style={{ color: 'var(--c-0ca30c)' }}>all up</strong>}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {ports.map(p => <PonCard key={p.pon} p={p} />)}
      </div>
    </div>
  );
}
